import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  SkeletonSpinnerCentered,
} from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import * as turf from '@turf/turf';
import Dinero from 'dinero.js';
import { map } from 'lodash';
import { type ReactNode, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { editProjectGeometry as apiEditProjectGeometry } from '~/api/projects2';
import { createCircleFromPoint } from '~/common/geoUtils';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { type CircleObject, type LineObject, type Project, ProjectGeometryType } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { useIsMounted } from '~/hooks/useIsMounted';
import type { ProjectDetailsParams } from '../..';
import { zeroCostCopy } from './common';
import store, { CHANGE_ORDER_FEE } from './store';

interface ConfirmationDialogProps {
  isDisabled: boolean;
  /** Shown on the disabled Save button to explain why it can't be used. */
  disabledReason?: string;
  costDifference: number;
  geometry: GeoJSON.MultiLineString | GeoJSON.MultiPolygon;
  project: Project;
  refetch: () => void;
}

export default function ConfirmationDialog({
  isDisabled,
  disabledReason,
  costDifference,
  geometry,
  project,
  refetch,
}: ConfirmationDialogProps) {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { objectsAsArray, geometryType, shouldAddChangeOrderFee } = useSnapshot(store) as typeof store;
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useIsMounted();

  const onSubmit = async () => {
    if (objectsAsArray.length === 0) return;

    try {
      setIsLoading(true);

      let geometryData: any = {};
      if (geometryType === ProjectGeometryType.Linear) {
        let bufferedPolygon = turf.buffer(geometry, (objectsAsArray[0] as LineObject).properties.buffer, {
          units: 'feet',
        })!.geometry;
        if (bufferedPolygon.type === 'Polygon') {
          bufferedPolygon = {
            type: 'MultiPolygon',
            coordinates: [bufferedPolygon.coordinates],
          };
        }
        geometryData = {
          ...geometryData,
          geometry: bufferedPolygon,
          linear: {
            geometry,
            buffer: {
              offset: (objectsAsArray[0] as LineObject).properties.buffer,
              units: 'feet',
            },
          },
        };
      } else {
        geometryData = {
          ...geometryData,
          geometry,
        };
      }

      // append circle data to the project information
      if (geometryType === ProjectGeometryType.Circle) {
        const circleMultiPolygon: GeoJSON.MultiPolygon = {
          type: 'MultiPolygon',
          coordinates: map(
            objectsAsArray as CircleObject[],
            (o) =>
              createCircleFromPoint({
                center: o.geometry,
                radius: o.properties.radius,
              }).coordinates,
          ),
        };
        const mp: GeoJSON.MultiPoint = {
          type: 'MultiPoint',
          coordinates: map(objectsAsArray, (o) => (o as CircleObject).geometry.coordinates),
        };
        const rad = (objectsAsArray[0] as CircleObject).properties.radius;

        const circleData: Project['circle'] = {
          geometry: mp,
          radius: { units: 'feet', value: rad },
        };

        geometryData = {
          ...geometryData,
          geometry: circleMultiPolygon,
          circle: { ...circleData },
        };
      }

      const res = await apiEditProjectGeometry(
        projectId,
        costDifference,
        geometryData.geometry,
        geometryData.linear,
        shouldAddChangeOrderFee,
        geometryData.circle,
      );

      // this tells the user that the data team will reach out regarding
      // existing data requests with the change in project boundary.
      if (res.notifyUser === true) {
        store.setAlertUserForMapRequests(true);
      }

      if (res.success === true) {
        Notify.info({
          title: 'Edit Project Geometry',
          message: 'Project geometry successfully updated.',
        });

        if (store.project.isLoading === false && res.transactionId) {
          capturePosthogEvent(POSTHOG_EVENTS.projectBoundarySaved, {
            project_id: store.project.id,
            organization_id: store.project.organization || undefined,
            transaction_id: res.transactionId,
            amount: costDifference,
          });
        }
      }
    } catch (e: any) {
      Notify.error({
        title: 'Edit Project Geometry',
        message: `There was an error saving your new project geometry: ${e.response?.data?.reason || 'Error'}`,
      });
    } finally {
      store.reset();
      refetch();

      if (isMounted()) {
        setIsLoading(false);
      }
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const content = useMemo(() => {
    if (isLoading) {
      return <SkeletonSpinnerCentered />;
    }

    let copy: ReactNode = '';

    if (project.billingTier === 'draft') {
      copy = 'Are you sure?';
    } else if (costDifference === 0 && !shouldAddChangeOrderFee) {
      copy = zeroCostCopy;
    } else {
      let total = Dinero({ amount: costDifference });
      if (shouldAddChangeOrderFee) {
        total = total.add(CHANGE_ORDER_FEE);
      }
      copy = (
        <div>
          There is an additional cost of <span className="font-bold">{total.toFormat()}</span> to edit this project
          boundary. Please confirm.
        </div>
      );
    }

    return <div className="space-y-4">{copy}</div>;
  }, [isLoading, costDifference]);

  return (
    <Dialog key={`edit-project-geometry-save`}>
      <DialogTrigger asChild>
        <Button disabled={isDisabled} tooltip={disabledReason} data-testid="project-edit-geometry-save-trigger">
          Save
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Edit Project Boundary"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid="project-edit-geometry-save-dialog"
      >
        <DialogBody className="text-sm">{content}</DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" disabled={isLoading} data-testid="project-edit-geometry-discard">
              Discard
            </Button>
          </DialogClose>
          <Button onClick={onSubmit} disabled={isLoading} data-testid="project-edit-geometry-confirm">
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
